import createApiService from "@lib/create-api-service";
import { MAX_TRIES } from "@lib/street-autocomplete/constants";
import { getToken } from "@lib/street-autocomplete/services/getToken";
import { removeTokenCookie } from "@lib/street-autocomplete/services/updateCookie";
import { ADDRESS_SEARCH } from "@utils/constants";
import { reportError } from "@utils/errorReporting";

import validateAddressesResponse from "../models/addresses";
import type { TAddressSearchEntry } from "../types";
import getAddressRequestFilter from "../utils/getAddressRequestFilter";
import mapAddressSearchResponse from "../utils/mapAddressSearchResponse";

type TAddressSearchParams = {
  query: string;
  countryCode: string;
  defaultLocale: string;
  errCallback?: () => void;
  count?: number;
};

const getAddresses = createApiService({
  method: "GET",
  baseURL: ADDRESS_SEARCH.ADDRESS_SEARCH_BASE_URL,
  url: "/autosuggest",
  isExternalApi: true,
});

const loadAddressOptions = async ({
  query,
  countryCode,
  defaultLocale,
  errCallback,
  count = 0,
}: TAddressSearchParams): Promise<TAddressSearchEntry[]> => {
  try {
    if (!query || !query.length) {
      return [];
    }

    const token = await getToken(defaultLocale);

    const response = await getAddresses({
      params: {
        q: query,
        at: ADDRESS_SEARCH.COORDINATES[defaultLocale],
        in: getAddressRequestFilter(countryCode || defaultLocale),
        types: "street,houseNumber",
        lang: "en-US",
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { Authorization: `Bearer ${token}` } as any,
    });
    if (!response.isSuccess) {
      if (response.statusCode === 401 && count < MAX_TRIES) {
        removeTokenCookie();
        const incrementedCount = count + 1;
        return await loadAddressOptions({ query, countryCode, defaultLocale, errCallback, count: incrementedCount });
      }
      throw new Error(response.error?.message);
    }

    const validResponse = validateAddressesResponse(response.data);

    return mapAddressSearchResponse(validResponse);
  } catch (err) {
    reportError(err, { module: "address-search" });
    errCallback?.();
    return [];
  }
};

export default loadAddressOptions;
